import { Copy, Ellipsis, Download, Link2, Maximize2 } from 'lucide-react'
import PropTypes from 'prop-types'
import { forwardRef, memo, useCallback, useMemo, useState } from 'react'

import { Button } from '@/components/elements/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/elements/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/elements/tooltip'
import { cn } from '@/utils/helpers'

export const ActionWrapper = memo(
  forwardRef(({ children, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'absolute top-3 right-3 flex flex-row rounded px-0.5 opacity-0 group-hover/node-image:opacity-100',
        'border-[0.5px] bg-[var(--mt-bg-secondary)] [backdrop-filter:saturate(1.8)_blur(20px)]',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )),
)

ActionWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
}

ActionWrapper.displayName = 'ActionWrapper'

export const ActionButton = memo(
  forwardRef(({ icon, tooltip, className, ...props }, ref) => (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          ref={ref}
          variant="ghost"
          className={cn(
            'text-muted-foreground hover:text-foreground relative flex h-7 w-7 flex-row rounded-none p-0',
            'bg-transparent hover:bg-transparent',
            className,
          )}
          {...props}
        >
          {icon}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">{tooltip}</TooltipContent>
    </Tooltip>
  )),
)

ActionButton.propTypes = {
  icon: PropTypes.node.isRequired,
  tooltip: PropTypes.string.isRequired,
  className: PropTypes.string,
}

ActionButton.displayName = 'ActionButton'

const ActionItems = [
  {
    key: 'onView',
    icon: <Maximize2 className="size-4" />,
    tooltip: 'View image',
  },
  {
    key: 'onDownload',
    icon: <Download className="size-4" />,
    tooltip: 'Download image',
  },
  {
    key: 'onCopy',
    icon: <Copy className="size-4" />,
    tooltip: 'Copy image to clipboard',
  },
  {
    key: 'onCopyLink',
    icon: <Link2 className="size-4" />,
    tooltip: 'Copy image link',
    isLink: true,
  },
]

export const ImageActions = memo(({ shouldMerge = false, isLink = false, ...actions }) => {
  const [isOpen, setIsOpen] = useState(false)

  const handleAction = useCallback((e, action) => {
    e.preventDefault()
    e.stopPropagation()
    action?.()
  }, [])

  const filteredActions = useMemo(
    () => ActionItems.filter((item) => isLink || !item.isLink),
    [isLink],
  )

  return (
    <ActionWrapper className={cn({ 'opacity-100': isOpen })}>
      {shouldMerge ? (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
          <DropdownMenuTrigger asChild>
            <ActionButton
              icon={<Ellipsis className="size-4" />}
              tooltip="Open menu"
              onClick={(e) => e.preventDefault()}
            />
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end">
            {filteredActions.map(({ key, icon, tooltip }) => (
              <DropdownMenuItem key={key} onClick={(e) => handleAction(e, actions[key])}>
                <div className="flex flex-row items-center gap-2">
                  {icon}
                  <span>{tooltip}</span>
                </div>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        filteredActions.map(({ key, icon, tooltip }) => (
          <ActionButton
            key={key}
            icon={icon}
            tooltip={tooltip}
            onClick={(e) => handleAction(e, actions[key])}
          />
        ))
      )}
    </ActionWrapper>
  )
})

ImageActions.propTypes = {
  shouldMerge: PropTypes.bool,
  isLink: PropTypes.bool,
}

ImageActions.displayName = 'ImageActions'
